import cx from 'classnames';
import type { FunctionComponent } from 'react';

import { type IconClassName, icons } from './Icon/icons';
import { Icon, IconAndNode } from './Icon/index';
import { Menu, MenuItem } from './Menu/index';

import styles from './SortingDropdown.module.less';

export type SortingDirection = 'asc' | 'desc';

export type Props = {
  direction?: SortingDirection;
  setDirection: (direction?: SortingDirection) => void;
};

/**
 * Reusable component for sorting an individual table column by asc or desc.
 *
 * @example
 * <SortingDropdown
 *   direction={getPackageSortingDirection()}
 *   setDirection={setPackageSortingDirection}
 * />
 *   <List.HeaderText>Package</List.HeaderText>
 * </SortingDropdown>
 */
const SortingDropdown: FunctionComponent<Props> = ({
  children,
  direction,
  setDirection,
}) => {
  const isAscending = direction === 'asc';
  const isDescending = direction === 'desc';
  const isActive = isAscending || isDescending;

  const setIsAscending = () => setDirection('asc');
  const setIsDescending = () => setDirection('desc');

  let menuButtonIcon: IconClassName = icons.anglesUpDown;
  if (isAscending) {
    menuButtonIcon = icons.arrowUp;
  } else if (isDescending) {
    menuButtonIcon = icons.arrowDown;
  }

  return (
    <div>
      <Menu
        direction="bottom"
        menuButton={(
          <div className={cx(styles.menuButton, { [styles.menuButtonActive]: isActive })}>
            <IconAndNode icon={menuButtonIcon} alignIcon="right" marginAmount="0.5em">
              {children}
            </IconAndNode>
          </div>
        )}
      >
        <MenuItem onClick={setIsAscending} className={cx(styles.menuItem, { [styles.menuItemActive]: isAscending })}>
          {/* Ensure the IconAndNode are contained within a single parent. */}
          <div>
            <IconAndNode icon={icons.arrowUp} marginAmount="0.5em">
              Ascending
            </IconAndNode>
          </div>
          {isAscending && <Icon icon={icons.check} />}
        </MenuItem>
        <MenuItem onClick={setIsDescending} className={cx(styles.menuItem, { [styles.menuItemActive]: isDescending })}>
          {/* Ensure the IconAndNode are contained within a single parent. */}
          <div>
            <IconAndNode icon={icons.arrowDown} marginAmount="0.5em">
              Descending
            </IconAndNode>
          </div>
          {isDescending && <Icon icon={icons.check} />}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default SortingDropdown;
